import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Key, Copy, Trash2, Plus, Clock, CheckCircle2, XCircle, Search, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { AdminLayout } from "./AdminLayout";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminInstallment() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const prefilledNoSamb = searchParams.get("no_samb") || "";
  
  const [codes, setCodes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(!!prefilledNoSamb);
  
  // Create code form state
  const [searchCustomer, setSearchCustomer] = useState(prefilledNoSamb);
  const [customerBills, setCustomerBills] = useState([]);
  const [selectedBills, setSelectedBills] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [customerInfo, setCustomerInfo] = useState(null);

  const token = localStorage.getItem("admin_token");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchCodes();
    if (prefilledNoSamb) {
      handleSearchCustomer();
    }
  }, []);

  const fetchCodes = async () => {
    try {
      const response = await axios.get(`${API}/admin/installment-codes`, { headers });
      setCodes(response.data);
    } catch (err) {
      toast.error("Gagal memuat data kode cicilan");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearchCustomer = async (e) => {
    if (e) e.preventDefault();
    if (!searchCustomer.trim()) {
      toast.error("Masukkan No. Sambungan");
      return;
    }

    setIsSearching(true);
    setCustomerBills([]);
    setSelectedBills([]);
    setCustomerInfo(null);

    try {
      const response = await axios.get(`${API}/customer/bills?no_samb=${searchCustomer.trim()}`);
      const bills = response.data.bills || [];
      
      // Filter only unpaid bills
      const unpaidBills = bills.filter(b => b.status === "unpaid");
      
      if (unpaidBills.length === 0) {
        toast.info("Tidak ada tagihan belum bayar untuk pelanggan ini");
        return;
      }
      
      setCustomerBills(unpaidBills);
      if (unpaidBills.length > 0) {
        setCustomerInfo({
          nama: unpaidBills[0].nama,
          no_samb: unpaidBills[0].no_samb,
          alamat: unpaidBills[0].alamat
        });
      }
    } catch (err) {
      if (err.response?.status === 404) {
        toast.error("Pelanggan tidak ditemukan");
      } else {
        toast.error("Gagal mencari pelanggan");
      }
    } finally {
      setIsSearching(false);
    }
  };

  const handleToggleBill = (billId) => {
    setSelectedBills(prev => 
      prev.includes(billId) 
        ? prev.filter(id => id !== billId)
        : [...prev, billId]
    );
  };

  const handleSelectAll = () => {
    if (selectedBills.length === customerBills.length) {
      setSelectedBills([]);
    } else {
      setSelectedBills(customerBills.map(b => b.id));
    }
  };

  const handleCreateCode = async () => {
    if (selectedBills.length === 0) {
      toast.error("Pilih minimal 1 tagihan");
      return;
    }

    setIsCreating(true);
    try {
      const response = await axios.post(`${API}/admin/installment-code`, {
        no_samb: searchCustomer.trim(),
        bill_ids: selectedBills
      }, { headers });

      toast.success(`Kode cicilan berhasil dibuat: ${response.data.code}`);
      
      // Copy code to clipboard
      navigator.clipboard.writeText(response.data.code);
      toast.info("Kode disalin ke clipboard!");
      
      // Reset form and refresh
      setShowCreateModal(false);
      setSearchCustomer("");
      setCustomerBills([]);
      setSelectedBills([]);
      setCustomerInfo(null);
      fetchCodes();
      
      // Remove query param
      navigate("/admin/installment", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal membuat kode cicilan");
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteCode = async (code) => {
    if (!window.confirm(`Hapus kode ${code}?`)) return;
    
    try {
      await axios.delete(`${API}/admin/installment-code/${code}`, { headers });
      toast.success("Kode berhasil dihapus");
      fetchCodes();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal menghapus kode");
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success("Kode disalin!");
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("id-ID", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "active":
        return <Badge className="bg-emerald-100 text-emerald-700">Aktif</Badge>;
      case "used":
        return <Badge className="bg-blue-100 text-blue-700">Sudah Dipakai</Badge>;
      case "expired":
        return <Badge className="bg-red-100 text-red-700">Kadaluarsa</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  // Calculate totals
  const selectedTotal = customerBills
    .filter(b => selectedBills.includes(b.id))
    .reduce((sum, b) => sum + b.jumlah + 2000, 0); // 2000 is admin fee per bill

  return (
    <AdminLayout title="Kode Cicilan">
      <div className="p-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Kode Cicilan</h1>
            <p className="text-slate-600">Buat kode untuk pembayaran cicilan pelanggan</p>
          </div>
          <Button 
            onClick={() => setShowCreateModal(true)}
            className="gap-2"
            data-testid="create-code-btn"
          >
            <Plus className="w-4 h-4" />
            Buat Kode Baru
          </Button>
        </div>

        {/* Create Code Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Key className="w-5 h-5" />
                  Buat Kode Cicilan Baru
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Search Customer */}
                <form onSubmit={handleSearchCustomer} className="flex gap-2">
                  <Input
                    placeholder="No. Sambungan"
                    value={searchCustomer}
                    onChange={(e) => setSearchCustomer(e.target.value)}
                    data-testid="search-customer-input"
                  />
                  <Button type="submit" disabled={isSearching}>
                    {isSearching ? (
                      <div className="spinner w-4 h-4" />
                    ) : (
                      <Search className="w-4 h-4" />
                    )}
                  </Button>
                </form>

                {/* Customer Info */}
                {customerInfo && (
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="font-medium text-slate-800">{customerInfo.nama}</p>
                    <p className="text-sm text-slate-600">No. Samb: {customerInfo.no_samb}</p>
                    {customerInfo.alamat && (
                      <p className="text-sm text-slate-500">{customerInfo.alamat}</p>
                    )}
                  </div>
                )}

                {/* Bills List */}
                {customerBills.length > 0 && (
                  <>
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-slate-600">Pilih tagihan untuk cicilan:</p>
                      <Button variant="ghost" size="sm" onClick={handleSelectAll}>
                        {selectedBills.length === customerBills.length ? "Batal Pilih Semua" : "Pilih Semua"}
                      </Button>
                    </div>

                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {customerBills.map((bill) => (
                        <div 
                          key={bill.id}
                          className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                            selectedBills.includes(bill.id) 
                              ? "border-sky-500 bg-sky-50" 
                              : "border-slate-200 hover:border-slate-300"
                          }`}
                          onClick={() => handleToggleBill(bill.id)}
                        >
                          <Checkbox 
                            checked={selectedBills.includes(bill.id)}
                            onCheckedChange={() => handleToggleBill(bill.id)}
                          />
                          <div className="flex-1">
                            <p className="font-medium text-slate-800">{bill.bulan}</p>
                            <p className="text-sm text-slate-500">Denda: {formatCurrency(bill.denda)}</p>
                          </div>
                          <p className="font-semibold text-slate-800">{formatCurrency(bill.jumlah + 2000)}</p>
                        </div>
                      ))}
                    </div>

                    {/* Selected Total */}
                    {selectedBills.length > 0 && (
                      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                        <div className="flex justify-between items-center">
                          <span className="text-emerald-700">
                            {selectedBills.length} tagihan dipilih
                          </span>
                          <span className="text-xl font-bold text-emerald-600">
                            {formatCurrency(selectedTotal)}
                          </span>
                        </div>
                        <p className="text-xs text-emerald-600 mt-1">
                          Sudah termasuk fee admin
                        </p>
                      </div>
                    )}
                  </>
                )}

                {/* Actions */}
                <div className="flex gap-3 pt-2">
                  <Button 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => {
                      setShowCreateModal(false);
                      setSearchCustomer("");
                      setCustomerBills([]);
                      setSelectedBills([]);
                      setCustomerInfo(null);
                      navigate("/admin/installment", { replace: true });
                    }}
                  >
                    Batal
                  </Button>
                  <Button 
                    className="flex-1"
                    disabled={selectedBills.length === 0 || isCreating}
                    onClick={handleCreateCode}
                    data-testid="confirm-create-code-btn"
                  >
                    {isCreating ? (
                      <div className="spinner w-4 h-4" />
                    ) : (
                      "Buat Kode"
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Codes List */}
        {isLoading ? (
          <div className="text-center py-12">
            <div className="spinner mx-auto mb-4" />
            <p className="text-slate-600">Memuat data...</p>
          </div>
        ) : codes.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Key className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600">Belum ada kode cicilan</p>
              <p className="text-sm text-slate-500 mt-1">Klik "Buat Kode Baru" untuk membuat kode cicilan</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {/* Active Codes */}
            <h2 className="text-lg font-semibold text-slate-800">Kode Aktif</h2>
            <div className="grid gap-4">
              {codes.filter(c => c.status === "active").map((code) => (
                <Card key={code.id} className="border-emerald-200">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="text-2xl font-mono font-bold text-emerald-600">{code.code}</span>
                          <button
                            onClick={() => copyCode(code.code)}
                            className="p-1.5 rounded-lg bg-emerald-100 hover:bg-emerald-200 text-emerald-600 transition-colors"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                          {getStatusBadge(code.status)}
                        </div>
                        <p className="font-medium text-slate-800">{code.nama}</p>
                        <p className="text-sm text-slate-600">No. Samb: {code.no_samb}</p>
                        <p className="text-sm text-slate-600">Periode: {code.periode_list}</p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                          <span>Dibuat: {formatDate(code.created_at)}</span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            Berlaku sampai: {formatDate(code.expires_at)}
                          </span>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-bold text-emerald-600">{formatCurrency(code.total_bayar)}</p>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-500 hover:text-red-700 hover:bg-red-50 mt-2"
                          onClick={() => handleDeleteCode(code.code)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {codes.filter(c => c.status === "active").length === 0 && (
                <p className="text-slate-500 text-center py-4">Tidak ada kode aktif</p>
              )}
            </div>

            {/* Used/Expired Codes */}
            {codes.filter(c => c.status !== "active").length > 0 && (
              <>
                <h2 className="text-lg font-semibold text-slate-800 mt-8">Riwayat Kode</h2>
                <div className="grid gap-4">
                  {codes.filter(c => c.status !== "active").map((code) => (
                    <Card key={code.id} className="opacity-75">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <span className="text-xl font-mono font-bold text-slate-500">{code.code}</span>
                              {getStatusBadge(code.status)}
                            </div>
                            <p className="font-medium text-slate-700">{code.nama}</p>
                            <p className="text-sm text-slate-500">No. Samb: {code.no_samb} | Periode: {code.periode_list}</p>
                            <p className="text-xs text-slate-400 mt-1">
                              Dibuat: {formatDate(code.created_at)}
                              {code.used_at && ` | Digunakan: ${formatDate(code.used_at)}`}
                            </p>
                          </div>
                          <p className="font-semibold text-slate-500">{formatCurrency(code.total_bayar)}</p>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
